/**
 * The detail panel for one entry. Read-only for viewers; owners get an edit
 * form in the same panel rather than a modal, so the ring stays visible.
 */
import type { CalEvent, Source, Tag } from '@circle/shared';
import { detailText } from '../core/format';
import { api, imageUrl } from '../app/api';
import { el, button, type Child } from './dom';
import { EventForm } from './event-form';
import { TagChips } from './tag-chips';
import { confirmDialog } from './dialog';
import { toast, toastError } from './toast';
import type { Panel } from './panel';

export interface DetailOptions {
  panel: Panel;
  calendarId: string;
  event: CalEvent;
  source: Source | undefined;
  viewYear: number;
  today: Date;
  /** Owners get edit and delete; viewers do not. */
  editable: boolean;
  /** The calendar's circles, read live. Absent: no circles shown. */
  tags?: () => Tag[];
  /**
   * A circle chip was clicked: light that entry's ring, or every ring again
   * with `null`. The ring ids are `'root'` or a tag id, as the scene has them.
   */
  onHighlightRing?: (ringId: string | null) => void;
  /** A circle was made from the edit form; the page refetches the bundle. */
  onTagsChanged?: () => Promise<void> | void;
  onChanged: (event: CalEvent) => void;
  onDeleted: (eventId: string) => void;
  onClose?: () => void;
}

export function avatar(event: CalEvent, size = 52): HTMLElement {
  if (event.imageKey) {
    return el('img', {
      class: 'avatar',
      src: imageUrl(event.imageKey),
      alt: '',
      width: String(size),
      height: String(size),
    });
  }
  const letter = event.title.trim().charAt(0).toUpperCase() || '·';
  return el('div', { class: 'avatar avatar--letter', 'aria-hidden': 'true', text: letter });
}

export function sourceBadge(source: Source | undefined): HTMLElement | null {
  if (!source) return null;
  return el(
    'span',
    { class: 'badge' },
    el('span', { class: 'swatch', style: `background:${source.colour}` }),
    el('span', { text: source.name }),
  );
}

export function openDetail(options: DetailOptions): void {
  const { panel, event, source } = options;
  const key = `event:${event.id}`;
  if (panel.openKey === key) {
    panel.close();
    return;
  }

  /** The ring currently lit from a chip; clicking the same chip puts it out. */
  let lit: string | null = null;

  /**
   * The circles this entry is in, as chips that point at rings.
   *
   * In `split` or `concentric` the entry is drawn once per circle, and "which
   * of those is this one?" is a fair question with no other answer. Clicking a
   * chip dims everything that is not on that ring.
   */
  const circles = (): Child => {
    const all = options.tags?.() ?? [];
    const mine = new Set(event.tagIds ?? []);
    const shown = all.filter((tag) => mine.has(tag.id));
    if (shown.length === 0) return null;
    const chips = new TagChips({
      tags: () => shown,
      selected: [],
      editable: false,
      onPick: (tagId) => {
        lit = lit === tagId ? null : tagId;
        options.onHighlightRing?.(lit);
        for (const chip of chips.root.querySelectorAll('.chip')) {
          chip.setAttribute('aria-pressed', chip.getAttribute('data-tag') === lit ? 'true' : 'false');
        }
      },
    });
    return el(
      'div',
      { class: 'field detail__circles' },
      el('span', { class: 'muted small', text: shown.length === 1 ? 'Circle' : 'Circles' }),
      chips.root,
    );
  };

  /**
   * Every `panel.open` from here goes through the same options, so the panel
   * always carries the caller's `onClose`. The edit view used to omit it, and
   * the close that followed a save then left the entry selected in the scene —
   * after which clicking the same float selected nothing new and the panel
   * never came back.
   */
  const openOptions = (): { key: string; onClose?: () => void } => ({
    key,
    onClose: () => {
      // A lit ring belongs to this panel; it goes out with it.
      if (lit !== null) {
        lit = null;
        options.onHighlightRing?.(null);
      }
      options.onClose?.();
    },
  });

  const show = (): void => {
    const body: Child[] = [
      el(
        'div',
        { class: 'detail__head' },
        avatar(event),
        el(
          'div',
          { class: 'stack', style: 'gap:2px;min-width:0' },
          el('div', { class: 'detail__title', text: event.title }),
          el('div', {
            class: 'detail__when',
            text: detailText(event, options.viewYear, options.today),
          }),
        ),
      ),
      el('div', { class: 'row' }, sourceBadge(source), event.kind === 'note' ? el('span', { class: 'badge', text: 'Note' }) : null),
      circles(),
      event.orphanedAt
        ? el('div', {
            class: 'notice notice--alert',
            text: 'This entry is gone from its feed. It stays here until you remove it.',
          })
        : null,
    ];

    const foot: Child[] = [];
    if (options.editable) {
      foot.push(
        button('Edit', () => edit(), { class: 'btn--small' }),
        el('span', { class: 'grow', style: 'flex:1' }),
        button(
          'Delete',
          async () => {
            const yes = await confirmDialog({
              title: `Delete ${event.title}?`,
              description: 'The entry leaves the ring for everyone who can see this calendar.',
              confirmLabel: 'Delete',
              danger: true,
            });
            if (!yes) return;
            try {
              await api.deleteEvent(options.calendarId, event.id);
              options.onDeleted(event.id);
              panel.close();
              toast(`Deleted ${event.title}.`);
            } catch (err) {
              toastError(err);
            }
          },
          { class: 'btn--small btn--danger' },
        ),
      );
    }

    panel.open({ title: 'Entry', body, ...(foot.length ? { foot } : {}) }, openOptions());
  };

  const edit = (): void => {
    const form = new EventForm({
      calendarId: options.calendarId,
      event,
      source,
      viewYear: options.viewYear,
      ...(options.tags ? { tags: options.tags } : {}),
      ...(options.onTagsChanged ? { onTagsChanged: options.onTagsChanged } : {}),
      onSaved: (updated) => {
        form.destroy();
        options.onChanged(updated);
        toast('Saved.');
        panel.close();
      },
      onCancel: () => {
        form.destroy();
        show();
      },
    });
    panel.open({ title: `Edit ${event.title}`, body: [form.root] }, openOptions());
    form.focus();
  };

  show();
}
